// src/XoneIsland.ts
var RUNTIME_KEY = "__xoneIslandRuntime";
var HYDRATED = /* @__PURE__ */ Symbol("xone-island-hydrated");
var ELEMENT_TAG = "xone-island";
var XoneIslandElement = class extends HTMLElement {
  [HYDRATED] = false;
  cleanup = [];
  connectedCallback() {
    if (this[HYDRATED]) return;
    const lazy = this.getAttribute("lazy") ?? "interaction";
    if (lazy === "eager") {
      void this.hydrate();
      return;
    }
    if (lazy === "visible") {
      this.observeVisible();
      return;
    }
    this.observeFirstInteraction();
  }
  disconnectedCallback() {
    for (const fn of this.cleanup) {
      try {
        fn();
      } catch {
      }
    }
    this.cleanup = [];
  }
  observeFirstInteraction() {
    const evts = [
      "focus",
      "click",
      "input",
      "change",
      "keydown"
    ];
    const onTrigger = () => {
      void this.hydrate();
      for (const e of evts) this.removeEventListener(e, onTrigger, true);
    };
    for (const e of evts) {
      this.addEventListener(e, onTrigger, { capture: true });
    }
  }
  observeVisible() {
    if (typeof IntersectionObserver === "undefined") {
      void this.hydrate();
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            obs.disconnect();
            void this.hydrate();
            return;
          }
        }
      },
      { rootMargin: "100px" }
    );
    obs.observe(this);
    this.cleanup.push(() => obs.disconnect());
  }
  async hydrate() {
    if (this[HYDRATED]) return;
    this[HYDRATED] = true;
    const runtime = window[RUNTIME_KEY];
    if (!runtime) {
      return;
    }
    try {
      this.wireListeners(runtime);
    } catch (err) {
      try {
        runtime.api.console.error("[xone-island] hydrate failed", err);
      } catch {
      }
    }
  }
  wireListeners(runtime) {
    const eventsSpec = (this.getAttribute("data-events") ?? "").split(",").map((s) => s.trim()).filter((s) => s !== "");
    const propName = this.getAttribute("data-prop") ?? "";
    const propType = (this.getAttribute("data-type") ?? "").toUpperCase();
    for (const spec of eventsSpec) {
      if (spec === "bind") {
        this.wireBind(runtime, propName, propType);
      } else if (spec === "click") {
        this.wireClickInline(runtime, propName);
      } else if (spec === "change") {
        this.wireChangeInline(runtime, propName);
      } else if (spec === "contents") {
        this.wireContents(runtime, propName);
      } else if (spec.startsWith("method:")) {
        const methodName = spec.slice("method:".length);
        this.wireMethod(runtime, propName, methodName);
      }
    }
  }
  wireBind(runtime, propName, propType) {
    if (propName === "") return;
    const input = this.querySelector(
      "input,textarea,select"
    );
    if (!input) return;
    const initial = safeGetValue(runtime.api, propName);
    if (initial !== void 0 && initial !== null) {
      if (propType === "NC" && "checked" in input) {
        input.checked = Boolean(initial);
      } else {
        input.value = String(initial);
      }
    }
    const onInput = () => {
      const v = propType === "NC" && "checked" in input ? input.checked : input.value;
      try {
        void runtime.api.self.setValue(propName, v);
      } catch (err) {
        runtime.api.console.error("[xone-island] bind setValue", err);
      }
      const changeHandler = runtime.events[`${propName}_onchange`];
      if (typeof changeHandler === "function") {
        void invokeHandler(runtime, changeHandler, [v]);
      }
    };
    const ev = propType === "NC" ? "change" : "input";
    input.addEventListener(ev, onInput);
    this.cleanup.push(() => input.removeEventListener(ev, onInput));
  }
  wireClickInline(runtime, propName) {
    const handlerName = `${propName}_onclick`;
    const handler = runtime.events[handlerName];
    if (typeof handler !== "function") {
      return;
    }
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = (e) => {
      void invokeHandler(runtime, handler, [e]);
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
  }
  wireChangeInline(runtime, propName) {
    const handlerName = `${propName}_onchange`;
    const handler = runtime.events[handlerName];
    if (typeof handler !== "function") return;
    const input = this.querySelector("input,textarea,select");
    if (!input) return;
    const onChange = () => {
      void invokeHandler(runtime, handler, [input.value]);
    };
    input.addEventListener("change", onChange);
    this.cleanup.push(() => input.removeEventListener("change", onChange));
  }
  wireMethod(runtime, propName, methodName) {
    const handler = runtime.events[methodName];
    if (typeof handler !== "function") return;
    const button = this.querySelector("button");
    const target = button ?? this;
    const onClick = () => {
      void invokeHandler(runtime, handler, []);
    };
    target.addEventListener("click", onClick);
    this.cleanup.push(() => target.removeEventListener("click", onClick));
    void propName;
  }
  wireContents(runtime, propName) {
    let srcColl = this.getAttribute("data-src") ?? "";
    let filter = this.getAttribute("data-filter") ?? void 0;
    let sort = this.getAttribute("data-sort") ?? void 0;
    if (!srcColl) {
      const innerDiv = this.querySelector("div[data-src]");
      if (innerDiv) {
        srcColl = innerDiv.getAttribute("data-src") ?? "";
        filter = filter ?? innerDiv.getAttribute("data-filter") ?? void 0;
        sort = sort ?? innerDiv.getAttribute("data-sort") ?? void 0;
      }
    }
    if (!srcColl) {
      this.setAttribute("data-status", "no-src");
      return;
    }
    this.setAttribute("data-status", "loading");
    const templateHtml = this.innerHTML;
    void (async () => {
      try {
        const coll = runtime.api.appData.getCollection(srcColl);
        await coll.loadAll(filter, sort);
        const count = coll.getCount?.() ?? 0;
        if (count === 0) {
          this.innerHTML = "";
          this.setAttribute("data-status", "empty");
          return;
        }
        this.innerHTML = "";
        if (coll.startBrowse) {
          await coll.startBrowse();
          while (coll.isEOF && !coll.isEOF()) {
            const item = coll.getCurrentItem?.();
            if (item) this.appendRow(templateHtml, item);
            if (coll.moveNext) await coll.moveNext();
            else break;
          }
          coll.endBrowse?.();
        } else {
          for (let i = 0; i < count; i++) {
            const item = coll.get?.(i);
            if (item) this.appendRow(templateHtml, item);
          }
        }
        this.setAttribute("data-status", "loaded");
        this.wireSelectedItem(runtime, propName);
      } catch (err) {
        this.setAttribute("data-status", "error");
        try {
          runtime.api.console.error("[xone-island] contents load failed", err);
        } catch {
        }
      }
    })();
  }
  appendRow(templateHtml, item) {
    const row = document.createElement("div");
    row.className = "xone-contents-row";
    row.innerHTML = templateHtml;
    const propEls = row.querySelectorAll("[data-prop]");
    for (const el of propEls) {
      const field = el.getAttribute("data-prop");
      if (!field) continue;
      const allFields = item;
      const value = allFields[field];
      if (value === void 0 || value === null) continue;
      const str = String(value);
      const textEl = el.querySelector(".xone-text-value");
      if (textEl) {
        textEl.textContent = str;
      } else if (el.tagName === "SPAN" || el.tagName === "DIV") {
        el.textContent = str;
      }
      const input = el.querySelector("input,textarea");
      if (input) input.value = str;
    }
    row.setAttribute("data-row-index", String(this.children.length));
    this.appendChild(row);
  }
  wireSelectedItem(runtime, propName) {
    const handler = runtime.events["selecteditem"];
    if (typeof handler !== "function") return;
    const onClick = (e) => {
      const target = e.target;
      const row = target.closest?.(".xone-contents-row");
      if (!row) return;
      const indexStr = row.getAttribute("data-row-index");
      void invokeHandler(runtime, handler, [indexStr ? Number(indexStr) : 0]);
    };
    this.addEventListener("click", onClick);
    this.cleanup.push(() => this.removeEventListener("click", onClick));
    void propName;
  }
};
function safeGetValue(api, prop) {
  try {
    return api.self.getValue(prop);
  } catch {
    return void 0;
  }
}
async function invokeHandler(runtime, handler, args) {
  try {
    await handler(runtime.api, ...args);
  } catch (err) {
    try {
      runtime.api.console.error("[xone-island] handler error", err);
    } catch {
    }
  }
}
var elementRegistered = false;
function registerXoneIsland(opts) {
  if (typeof window === "undefined") {
    return;
  }
  window[RUNTIME_KEY] = { api: opts.api, events: opts.events };
  if (elementRegistered) return;
  elementRegistered = true;
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, XoneIslandElement);
  }
}
function _resetXoneIslandForTests() {
  if (typeof window !== "undefined") {
    delete window[RUNTIME_KEY];
  }
  elementRegistered = false;
}
export {
  XoneIslandElement,
  _resetXoneIslandForTests,
  registerXoneIsland
};
//# sourceMappingURL=index.js.map